'use client'

import { Helmet } from 'react-helmet-async'
import Header from './LandingPage/Layout/Header'
import Footer from './LandingPage/Layout/Footer'
import WhatsAppButton from './LandingPage/UI/WhatsAppButton'
import { DISPLAY_PHONE } from '../../config/contact'

export default function Privacy() {
  return (
    <div className="bg-background-50">
      <Helmet>
        <title>Privacy Policy | ScanMaadi</title>
        <meta name="description" content="Privacy Policy for ScanMaadi — learn how we collect, use, store, and protect your personal information on our digital menu platform." />
      </Helmet>

      <Header />

      <main className="pt-28 pb-16 sm:pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-800">
              Privacy Policy
            </h1>
            <p className="mt-4 text-text-500 text-sm">
              Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: 11 February 2026
            </p>
          </div>

          <div className="prose-custom space-y-8 text-text-600 leading-relaxed">
            {/* 1. Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">1. Introduction</h2>
              <p>
                ScanMaadi ("<strong>Platform</strong>") is operated by Montivo Labs LLP
                ("<strong>Company</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"),
                a limited liability partnership registered under the Limited Liability Partnership Act, 2008,
                with its registered office at Bangalore, Karnataka, India.
              </p>
              <p className="mt-4">
                This Privacy Policy explains how we collect, use, store, share, and protect your personal
                information when you use the ScanMaadi platform, website (scanmaadi.com), and all related
                services. This policy applies to all users of the Platform, including registered Vendors and
                End Users who view digital menus by scanning QR codes.
              </p>
              <p className="mt-4">
                This Privacy Policy is published in compliance with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>The Information Technology Act, 2000 ("<strong>IT Act</strong>")</li>
                <li>The Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 ("<strong>SPDI Rules</strong>")</li>
                <li>The Digital Personal Data Protection Act, 2023 ("<strong>DPDP Act</strong>")</li>
              </ul>
              <p className="mt-4">
                By accessing or using the Platform, you consent to the collection and processing of your
                information as described in this Privacy Policy. Please also read our{' '}
                <a href="/terms" className="text-primary-500 hover:text-accent-500 underline">Terms of Service</a>{' '}
                which govern your use of the Platform.
              </p>
            </section>

            {/* 2. Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">2. Information We Collect</h2>
              <p>
                We collect different types of information depending on how you interact with the Platform:
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.1 Account and Profile Information</h3>
              <p>When you register for an account, we collect:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>First name and last name</li>
                <li>Phone number (used as your primary identifier and for phone-based verification)</li>
                <li>Profile picture (if you choose to upload one)</li>
                <li>User role within the Platform (e.g., Vendor, Operations, Admin)</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.2 Vendor Business Information</h3>
              <p>If you register as a Vendor, we additionally collect:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Business/restaurant name and description</li>
                <li>Owner's first name and last name</li>
                <li>Business contact number</li>
                <li>Business address and geo-location coordinates</li>
                <li>UPI ID (for payment and refund processing)</li>
                <li>Store and restaurant images</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.3 Menu Content</h3>
              <p>Vendors upload the following information to create their digital menus:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Menu item names, descriptions, and prices</li>
                <li>Menu item images</li>
                <li>Menu categories and sub-categories</li>
                <li>Allergen and nutrition information (if provided)</li>
                <li>Item variants and add-ons</li>
                <li>Item availability status</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.4 Order and Payment Information</h3>
              <p>When you make a purchase on the Platform, we collect:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Order details (items purchased, quantities, amounts)</li>
                <li>Payment status and transaction identifiers</li>
                <li>Subscription details (plan type, subscription date, renewal date, grace period expiry)</li>
              </ul>
              <p className="mt-3">
                <strong>Important:</strong> We do not store your UPI PIN, bank account details, or payment
                credentials. All payment processing is handled by our third-party payment gateway.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.5 QR Code Scan Analytics</h3>
              <p>
                When an End User scans a Vendor's QR code and views the menu, we collect only
                <strong> anonymised scan data</strong>, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Total number of scans per QR code</li>
                <li>Scan timestamps (date and time)</li>
              </ul>
              <p className="mt-3">
                We do <strong>not</strong> collect any personal information from End Users who scan QR codes.
                No name, phone number, device identifier, or location data is collected from menu viewers.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">2.6 Automatically Collected Information</h3>
              <p>
                When you use the Platform, we automatically collect certain technical and usage information
                through our analytics provider:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Device type, operating system, and browser type</li>
                <li>IP address</li>
                <li>Pages visited and navigation patterns within the Platform</li>
                <li>Session duration and page interaction data</li>
                <li>Referring URLs</li>
              </ul>
              <p className="mt-3">
                Our analytics provider uses input masking to ensure that sensitive form data (such as passwords
                and personal details) is not captured during session recordings.
              </p>
            </section>

            {/* 3. How We Collect Information */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">3. How We Collect Information</h2>
              <p>We collect information through the following methods:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Directly from you:</strong> When you register an account, create or update your profile,
                  upload menu content, place orders, or contact our support team.
                </li>
                <li>
                  <strong>Automatically:</strong> Through our analytics tools when you browse and interact with the
                  Platform. Our analytics tools collect pageview data, session information,
                  and user interaction events. All form inputs are masked in session recordings to protect sensitive data.
                </li>
                <li>
                  <strong>From third-party services:</strong> We receive payment confirmation and status updates
                  from our payment gateway and authentication data from our identity management provider.
                </li>
              </ul>
            </section>

            {/* 4. How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">4. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.1 Service Provision</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>To create and manage your account</li>
                <li>To host and display your digital menu to End Users</li>
                <li>To generate and manage QR codes linked to your menu</li>
                <li>To provide AI-powered menu digitisation services</li>
                <li>To provide scan analytics and reporting on your dashboard</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.2 Payment Processing</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>To process subscription payments and QR product purchases</li>
                <li>To manage subscription renewals and grace periods</li>
                <li>To process refunds where applicable</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.3 Communication</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>To send service-related notifications (subscription renewal reminders, payment confirmations)</li>
                <li>To respond to your support requests and inquiries</li>
                <li>To notify you of changes to our Terms, Privacy Policy, or Service</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.4 Analytics and Improvement</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>To understand how users interact with the Platform</li>
                <li>To identify and fix technical issues</li>
                <li>To improve our features, interface, and overall user experience</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">4.5 Security and Compliance</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>To detect and prevent fraud, abuse, and unauthorised access</li>
                <li>To enforce our Terms of Service</li>
                <li>To comply with applicable laws, regulations, and legal processes</li>
              </ul>
            </section>

            {/* 5. Information Sharing and Disclosure */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">5. Information Sharing and Disclosure</h2>
              <p>
                We do <strong>not sell, rent, or trade</strong> your personal information to any third party
                for their marketing or commercial purposes.
              </p>
              <p className="mt-4">We may share your information in the following limited circumstances:</p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.1 With End Users</h3>
              <p>
                When an End User scans your QR code, only your <strong>menu content</strong> (item names,
                descriptions, prices, images, and categories) is displayed. Your personal information
                (phone number, address, UPI ID, owner name) is <strong>not</strong> visible to End Users.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.2 With Service Providers</h3>
              <p>
                We use trusted third-party service providers to operate the Platform. These providers
                have access to your information only to perform specific tasks on our behalf and are
                contractually obligated to protect your data. Our service providers include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Cloud Hosting and Storage:</strong> We use industry-leading cloud infrastructure
                  providers to host the Platform, store your data, and manage user authentication. Your data
                  is stored on secure servers located in India.
                </li>
                <li>
                  <strong>Payment Processing:</strong> We use a third-party payment gateway to process UPI
                  transactions, including order creation, payment verification, and refund processing.
                </li>
                <li>
                  <strong>Analytics:</strong> We use analytics tools to understand user behaviour and improve
                  the Platform. Our analytics tools are configured to mask all sensitive form inputs in
                  session recordings.
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.3 For Legal Requirements</h3>
              <p>We may disclose your information if required to do so by law, or in good faith belief that such action is necessary to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Comply with a legal obligation, court order, or order of a government authority</li>
                <li>Protect and defend the rights or property of the Company</li>
                <li>Prevent or investigate possible wrongdoing in connection with the Platform</li>
                <li>Protect the personal safety of users or the public</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.4 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, reorganisation, or sale of assets, your personal
                information may be transferred to the acquiring entity. We will notify you via email or
                a prominent notice on the Platform before your information becomes subject to a different
                privacy policy.
              </p>
            </section>

            {/* 6. Data Storage and Security */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">6. Data Storage and Security</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.1 Where We Store Your Data</h3>
              <p>
                Your data is stored on <strong>secure servers located in India</strong>. This includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Structured data such as account information, orders, and subscriptions in secure databases</li>
                <li>Files such as menu images, profile pictures, and store images in secure file storage</li>
                <li>User authentication data in our identity management system</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.2 Security Measures</h3>
              <p>
                We implement reasonable security practices and procedures as required under the SPDI Rules
                and the DPDP Act to protect your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is encrypted using industry-standard encryption protocols.</li>
                <li><strong>Authentication:</strong> Industry-standard authentication and secure token management to verify user identity.</li>
                <li><strong>Access Controls:</strong> Access controls ensure that users can only access data and features permitted by their role (Vendor, Operations, Admin).</li>
                <li><strong>Payment Security:</strong> Payment credentials are never stored on our servers. All payment processing is handled by our payment gateway provider, which complies with payment industry security standards.</li>
                <li><strong>Analytics Privacy:</strong> Our analytics tools are configured to mask sensitive form data, ensuring that personal details are not captured during session recordings.</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.3 Browser Storage</h3>
              <p>
                The Platform stores session tokens in your browser to maintain your logged-in session.
                These tokens are automatically removed when you log out.
              </p>
            </section>

            {/* 7. Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">7. Data Retention</h2>
              <p>We retain your personal information as follows:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Active Accounts:</strong> Your data is retained for the duration of your active
                  Subscription and as long as your account remains active on the Platform.
                </li>
                <li>
                  <strong>Post-Termination:</strong> Upon account termination (whether by you or by us),
                  your data is retained for 30 (thirty) days. During this period, you may request a copy
                  of your data. After the 30-day retention period, your data may be permanently deleted
                  from our systems.
                </li>
                <li>
                  <strong>Payment and Transaction Records:</strong> We retain order and payment records for
                  the period required under applicable Indian tax and accounting laws (currently up to 8 years
                  under the Income Tax Act, 1961, and the GST Act, 2017).
                </li>
                <li>
                  <strong>Analytics Data:</strong> Aggregated and anonymised analytics data (such as scan counts)
                  may be retained indefinitely as it cannot be used to identify any individual.
                </li>
                <li>
                  <strong>Legal Obligations:</strong> We may retain certain data beyond the periods stated above
                  if required by law, regulation, or legal proceedings.
                </li>
              </ul>
            </section>

            {/* 8. Cookies and Tracking Technologies */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">8. Cookies and Tracking Technologies</h2>
              <p>The Platform uses the following technologies to store data on your device:</p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">8.1 Session Storage</h3>
              <p>
                We use your browser's storage to store session tokens that keep you logged in.
                These tokens are essential for the Platform to function and are removed when you log out.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">8.2 Analytics Cookies</h3>
              <p>
                Our analytics provider may set cookies to track your session and understand how
                you use the Platform. These cookies help us improve the user experience.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">8.3 No Advertising Cookies</h3>
              <p>
                We do <strong>not</strong> use any third-party advertising cookies or tracking pixels.
                We do not serve advertisements on the Platform and do not share your data with advertising networks.
              </p>
            </section>

            {/* 9. Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">9. Your Rights</h2>
              <p>
                Under the Digital Personal Data Protection Act, 2023, and other applicable Indian laws,
                you have the following rights with respect to your personal data:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Right to Access:</strong> You have the right to request a summary of the personal
                  data we hold about you and the processing activities related to it.
                </li>
                <li>
                  <strong>Right to Correction:</strong> You have the right to request correction of any
                  inaccurate or incomplete personal data. You can update most of your profile information
                  directly through the Platform.
                </li>
                <li>
                  <strong>Right to Erasure:</strong> You have the right to request deletion of your personal
                  data, subject to our legal obligations to retain certain records (such as payment and
                  tax records).
                </li>
                <li>
                  <strong>Right to Withdraw Consent:</strong> You may withdraw your consent to the processing
                  of your personal data at any time by contacting us. Please note that withdrawal of consent
                  may affect your ability to use certain features of the Platform.
                </li>
                <li>
                  <strong>Right to Data Portability:</strong> Upon account termination, you may request a copy
                  of your data in a commonly used, machine-readable format within the 30-day post-termination
                  retention period.
                </li>
                <li>
                  <strong>Right to Nominate:</strong> Under the DPDP Act, you have the right to nominate
                  another individual to exercise your data protection rights on your behalf in the event of
                  your death or incapacity.
                </li>
                <li>
                  <strong>Right to Grievance Redressal:</strong> You have the right to lodge a complaint
                  with our Grievance Officer (see Section 14 below) or with the Data Protection Board of
                  India if you believe your data rights have been violated.
                </li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">hello@scanmaadi.in</a>.
                We will respond to your request within 30 days.
              </p>
            </section>

            {/* 10. Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">10. Children's Privacy</h2>
              <p>
                The Platform is not intended for use by individuals under the age of 18. We do not knowingly
                collect personal data from children. If you are a parent or guardian and believe that your
                child has provided us with personal information, please contact us at{' '}
                <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">hello@scanmaadi.in</a>,
                and we will take steps to delete such information from our systems.
              </p>
            </section>

            {/* 11. Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">11. Third-Party Links and Services</h2>
              <p>
                The Platform may contain links to third-party websites or services, including but not
                limited to messaging services for customer support and payment gateways for payment processing.
                These third-party services have their own privacy policies, and we are not responsible for their
                privacy practices. We encourage you to review the privacy policies of any third-party
                services you interact with through the Platform.
              </p>
            </section>

            {/* 12. End User Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">12. End User (Menu Viewer) Privacy</h2>
              <p>
                We are committed to protecting the privacy of End Users who scan QR codes and view digital
                menus on the Platform:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>No registration required:</strong> End Users can view digital menus without creating an account, downloading an app, or providing any personal information.</li>
                <li><strong>No personal data collection:</strong> We do not collect names, phone numbers, email addresses, device identifiers, or precise location data from End Users.</li>
                <li><strong>Anonymised analytics only:</strong> We collect only aggregate scan counts and timestamps to provide Vendors with basic analytics. This data cannot be used to identify individual End Users.</li>
                <li><strong>Browser-based access:</strong> Digital menus are accessed through a standard web browser, and no cookies or tracking tokens are set for End Users who are not logged in to the Platform.</li>
              </ul>
            </section>

            {/* 13. Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">13. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices,
                technologies, legal requirements, or other factors. When we make material changes, we will:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Update the "Last Updated" date at the top of this page.</li>
                <li>Notify registered Vendors via email or through a prominent notice on the Platform.</li>
                <li>Where required by law, obtain your consent to the updated policy before continuing to process your data.</li>
              </ul>
              <p className="mt-4">
                Your continued use of the Platform after the effective date of the revised Privacy Policy
                constitutes your acceptance of the changes. If you do not agree with the updated policy,
                you should discontinue use of the Platform and contact us to delete your account.
              </p>
            </section>

            {/* 14. Grievance Officer */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">14. Grievance Officer</h2>
              <p>
                In accordance with the Information Technology Act, 2000, the Information Technology
                (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, and the Digital
                Personal Data Protection Act, 2023, we have appointed a Grievance Officer to address
                any concerns or complaints regarding the processing of your personal data.
              </p>
              <div className="mt-4 bg-background-100 rounded-xl p-6 border border-background-200">
                <p className="font-semibold text-text-800">Grievance Officer</p>
                <p className="text-text-600 mt-1">Montivo Labs LLP (Operating as ScanMaadi)</p>
                <div className="mt-4 space-y-2 text-text-600">
                  <p>
                    Email:{' '}
                    <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">
                      hello@scanmaadi.in
                    </a>
                  </p>
                  <p>Phone: {DISPLAY_PHONE}</p>
                  <p>Address: Bangalore, Karnataka, India</p>
                </div>
              </div>
              <p className="mt-4">
                The Grievance Officer will acknowledge your complaint within 24 hours and resolve it
                within 30 days from the date of receipt, in accordance with applicable law.
              </p>
            </section>

            {/* 15. Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">15. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our
                data practices, please contact us:
              </p>
              <div className="mt-4 bg-background-100 rounded-xl p-6 border border-background-200">
                <p className="font-semibold text-text-800">Montivo Labs LLP</p>
                <p className="text-text-600 mt-1">(Operating as ScanMaadi)</p>
                <div className="mt-4 space-y-2 text-text-600">
                  <p>Registered Office: Bangalore, Karnataka, India</p>
                  <p>
                    Email:{' '}
                    <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">
                      hello@scanmaadi.in
                    </a>
                  </p>
                  <p>Phone: {DISPLAY_PHONE}</p>
                </div>
              </div>
            </section>

            {/* Closing */}
            <section className="mt-12 pt-8 border-t border-background-200">
              <p className="text-text-500 text-sm">
                By using ScanMaadi, you acknowledge that you have read and understood this Privacy Policy
                and consent to the collection, use, and processing of your information as described herein.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  )
}
